// Markdown renderer for Ask Your Data assistant text.
//
// The LLM emits rich markdown (bold, numbered + bulleted lists,
// horizontal rules, inline code, fenced code blocks, headings, even
// emoji). The hand-rolled `llmToHTML` helper used elsewhere in the
// app only handles paragraphs + plain bullets, which mangles things
// like:
//
//     **Top picks**
//     1. **is_anomaly**
//        - high-value use case
//     ---
//
// We delegate to react-markdown + remark-gfm so all standard markdown
// just works. MUI components are used for the rendered output so the
// styling matches the rest of the app.

import React, { useState } from 'react';
import {
  Box, Typography, Link, Divider,
  List, ListItem, ListItemText,
  IconButton, Stack, Tooltip, alpha,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


// ---------------------------------------------------------------------------
// Fenced code blocks render as an "editor card": a slim header bar
// (traffic-light dots + language label + copy) above a dark code
// surface. The surface stays dark in BOTH themes - code reads best on
// dark, and it visually matches the Analyst redesign.
// ---------------------------------------------------------------------------

const extractText = (node) => {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node.props?.children != null) return extractText(node.props.children);
  return '';
};

const langOf = (node) => {
  const candidates = Array.isArray(node) ? node : [node];
  for (const c of candidates) {
    const m = /language-([\w+-]+)/.exec(c?.props?.className || '');
    if (m) return m[1];
  }
  return null;
};

const CodeCard = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const lang = langOf(children);

  const handleCopy = () => {
    const text = extractText(children).replace(/\n$/, '');
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => {});
  };

  return (
    <Box
      sx={(t) => ({
        my: 1.25,
        borderRadius: '12px',
        overflow: 'hidden',
        border: 1,
        borderColor: t.palette.mode === 'dark' ? '#262b26' : alpha('#0a0c0a', 0.15),
      })}
    >
      <Stack
        direction="row" alignItems="center" spacing={1}
        sx={{
          px: 1.5, py: 0.6,
          bgcolor: '#171a17',
          borderBottom: '1px solid #262b26',
        }}
      >
        <Stack direction="row" spacing={0.55}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6057' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#febc2e' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#28c840' }} />
        </Stack>
        <Typography
          variant="caption"
          sx={{ fontFamily: 'monospace', fontSize: 10.5, color: '#96a096' }}
        >
          {lang || 'code'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={copied ? 'Copied' : 'Copy code'} arrow>
          <IconButton
            size="small" onClick={handleCopy}
            sx={{ p: 0.4, color: copied ? '#85d818' : '#6b756b', '&:hover': { color: '#e9ede7' } }}
          >
            {copied ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0, p: 1.75,
          bgcolor: '#111411',
          color: '#e6ebe4',
          fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.7,
          overflowX: 'auto',
          whiteSpace: 'pre',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};


const components = {
  h1: ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 700, mt: 1.5, mb: 0.75 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.25, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="body2" sx={{ fontWeight: 700, mt: 1, mb: 0.25 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body1" sx={{ my: '10px', lineHeight: 1.65, fontSize: 14, color: 'inherit' }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="span" sx={{ fontWeight: 700 }}>{children}</Box>
  ),
  em: ({ children }) => (
    <Box component="span" sx={{ fontStyle: 'italic' }}>{children}</Box>
  ),
  hr: () => <Divider sx={{ my: 1.25 }} />,
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
  // GitHub-flavoured-markdown puts numbered lists inside <ol> and
  // bulleted ones inside <ul>. We render both with MUI <List> so
  // padding/spacing matches the rest of the chat bubble. We pass
  // `component="ol"` / `"ul"` so semantics survive.
  ul: ({ children }) => (
    <List
      component="ul"
      dense disablePadding
      sx={{ pl: 3, listStyleType: 'disc',
            '& li': { display: 'list-item', py: 0.25, px: 0 } }}
    >
      {children}
    </List>
  ),
  ol: ({ children }) => (
    <List
      component="ol"
      dense disablePadding
      sx={{ pl: 3, listStyleType: 'decimal',
            '& li': { display: 'list-item', py: 0.25, px: 0 } }}
    >
      {children}
    </List>
  ),
  li: ({ children }) => (
    <ListItem disableGutters disablePadding>
      <ListItemText
        primary={children}
        primaryTypographyProps={{ variant: 'body1', sx: { lineHeight: 1.65, fontSize: 14, color: 'inherit' } }}
      />
    </ListItem>
  ),
  code: ({ inline, children }) => {
    if (inline) {
      return (
        <Box
          component="code"
          sx={{
            px: 0.5, py: 0.1, mx: 0.25,
            borderRadius: 0.5,
            bgcolor: 'rgba(127,127,127,0.18)',
            fontFamily: 'monospace',
            fontSize: '0.85em',
          }}
        >
          {children}
        </Box>
      );
    }
    return <code>{children}</code>;
  },
  pre: ({ children }) => <CodeCard>{children}</CodeCard>,
  blockquote: ({ children }) => (
    <Box
      sx={{
        my: 0.75, pl: 1.5, py: 0.25,
        borderLeft: 3, borderColor: 'divider',
        color: 'text.secondary', fontStyle: 'italic',
      }}
    >
      {children}
    </Box>
  ),
  // Airy row-separated tables (no cell grid) matching the redesign.
  table: ({ children }) => (
    <Box
      component="table"
      sx={{
        my: 1, borderCollapse: 'collapse', width: '100%',
        '& th': {
          textAlign: 'left', fontWeight: 500,
          fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6,
          color: 'text.secondary',
          px: 1, py: 0.75,
          borderBottom: 1, borderColor: 'divider',
        },
        '& td': {
          px: 1, py: 1, fontSize: 13, verticalAlign: 'top',
          borderBottom: 1, borderColor: 'divider',
        },
        '& tbody tr:last-of-type td': { borderBottom: 'none' },
      }}
    >
      {children}
    </Box>
  ),
};


const MarkdownBody = ({ children }) => (
  <Box
    sx={(t) => ({
      // Sketch body text: #e9ede7 at 14px/1.65 on the ink canvas.
      color: t.palette.mode === 'dark' ? '#e9ede7' : t.palette.text.primary,
      '& > :first-of-type': { mt: 0 },
      '& > :last-of-type': { mb: 0 },
    })}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children || ''}
    </ReactMarkdown>
  </Box>
);

// Memoised: during streaming the reveal loop re-renders the LIVE text
// block ~30x/sec; every other (finished) block keeps its parsed tree.
export default React.memo(MarkdownBody);
